import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE, IMAGES } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { LanguageToggle } from "@/components/LanguageToggle";
import { OrderButton } from "@/components/TrackedLinks";
import { navItems } from "@/components/navItems";

const desktopLinkCls = ({ isActive }) => `nav-link font-medium transition-colors ${isActive ? "text-hacienda-terracotta" : "text-hacienda-gray hover:text-hacienda-terracotta"}`;
const mobileLinkCls = ({ isActive }) => `block py-3 px-4 rounded-lg font-medium transition-colors ${isActive ? "bg-hacienda-cream text-hacienda-terracotta" : "text-hacienda-gray hover:bg-hacienda-cream"}`;

const Brand = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link" aria-label={`${SITE.name} home`}>
    <img src={IMAGES.logo} alt={`${SITE.name} logo`} width="56" height="56" className="h-14 w-14 object-contain rounded-full bg-white" />
    <span className="hidden sm:block font-serif text-2xl font-semibold text-hacienda-charcoal leading-none">
      Tijuas Tacos<span className="block text-xs font-sans font-medium uppercase tracking-[0.25em] text-hacienda-terracotta mt-1">Birrieria</span>
    </span>
  </Link>
);

const MobileMenu = () => {
  const { t } = useLang();
  return (
    <div id="mobile-menu" className="lg:hidden bg-hacienda-white border-t border-hacienda-border-light" data-testid="mobile-menu">
      <nav className="px-4 py-4 space-y-1" aria-label="Mobile">
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === "/"} className={mobileLinkCls} data-testid={`mobile-nav-link-${item.key}`}>
            {t(`nav.${item.key}`)}
          </NavLink>
        ))}
        <div className="flex items-center justify-between pt-3 px-4">
          <LanguageToggle />
          <OrderButton location="mobile_menu" size="sm" data-testid="mobile-order-btn" />
        </div>
      </nav>
    </div>
  );
};

export const Header = ({ pathname }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setOpen(false);
  }

  return (
    <header className="sticky top-0 z-50 bg-hacienda-white/95 backdrop-blur-md border-b border-hacienda-border-light" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Brand />
          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={desktopLinkCls} data-testid={`nav-link-${item.key}`}>
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LanguageToggle className="hidden sm:inline-flex" />
            <OrderButton location="header" size="sm" className="hidden sm:inline-flex" data-testid="header-order-btn" />
            <button
              className="lg:hidden p-2 text-hacienda-charcoal rounded-md hover:bg-hacienda-cream transition-colors"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      {open && <MobileMenu />}
    </header>
  );
};
